import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Stack, Inline, Text, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { IconButton } from '@atlaskit/button/new';
import { Card } from '@atlaskit/smart-card';

import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers';
import { fg } from '@confluence/feature-gating';

import { useAudioBriefing } from '../../../hooks/useAudioBriefing';
import { RestartBriefingButton } from '../sub-components/RestartBriefingButton';
import { getContentUrl } from '../../../utils/getContentUrl';
import { AudioFeature } from '../../../types/audioFeatureType';

import { AdditionalResourceSelection } from './sources/sub-components/AdditionalResourceSelection';
import { BriefingSourcesInput } from './sources/sub-components/BriefingSourcesInput';

const styles = cssMap({
	container: {
		padding: token('space.200'),
	},
	sourcesContentContainer: {
		paddingInline: token('space.075'),
		paddingBlockStart: token('space.150'),
	},
	cardContainer: {
		overflow: 'hidden',
		minHeight: '27px', // same height as the Card component, to prevent layout shift
	},
});

const i18n = defineMessages({
	sourcesHeader: {
		id: 'audio.audio-popup.sources-header',
		defaultMessage: 'Sources',
		description: 'The header for the sources panel in the audio popup',
	},
	backButtonLabel: {
		id: 'audio.audio-popup.back-button-label',
		defaultMessage: 'Back',
		description: 'The label for the back button in the sources panel',
	},
	sourcesDescription: {
		id: 'audio.audio-popup.sources-description',
		defaultMessage: 'Customize briefings by adding sources. Hear how all the info works together.',
		description: 'The description for the sources panel in the audio popup',
	},
	thisPageHeader: {
		id: 'audio.audio-popup.sources-this-page-header',
		defaultMessage: 'This page',
		description: 'The header for the this page section in the sources panel',
	},
	additionalSourcesHeader: {
		id: 'audio.audio-popup.sources-additional-sources-header',
		defaultMessage: 'Additional sources',
		description: 'The header for the additional sources section in the sources panel',
	},
});

export type AudioBriefingSourcesPanelProps = {
	onBack: () => void;
	audioRef: React.RefObject<HTMLAudioElement>;
};

export const AudioBriefingSourcesPanel = ({ onBack, audioRef }: AudioBriefingSourcesPanelProps) => {
	const { formatMessage } = useIntl();

	const [
		{
			activeContent,
			additionalResources,
			cloudId,
			hasFetched,
			activeFeature,
			lastGeneratedBriefingConfig,
		},
		{ addAdditionalResource, removeAdditionalResource },
	] = useAudioBriefing();

	const haveSourceCountsChanged =
		additionalResources.length !== lastGeneratedBriefingConfig?.additionalResources?.length;

	const haveSourceContentsChanged = (lastGeneratedBriefingConfig?.additionalResources ?? []).some(
		(lastGeneratedResource, index) =>
			additionalResources?.[index]?.ari !== lastGeneratedResource.ari,
	);

	const sourcesHaveDiverged = haveSourceCountsChanged || haveSourceContentsChanged;

	const shouldShowRestartButton =
		hasFetched && activeFeature === AudioFeature.BRIEFING && sourcesHaveDiverged;

	const smartCardProviderProps = getSmartCardRenderers();

	const activeContentUrl = activeContent ? getContentUrl(activeContent) : null;

	const content = (
		<Box xcss={styles.container}>
			<Stack space="space.050">
				<Inline space="space.100" alignBlock="center">
					<IconButton
						icon={ChevronLeftIcon}
						onClick={onBack}
						appearance="subtle"
						label={formatMessage(i18n.backButtonLabel)}
						spacing="compact"
						testId="audio-briefing-sources-back-button"
					/>
					<Heading size="xsmall">{formatMessage(i18n.sourcesHeader)}</Heading>
				</Inline>
				<Stack xcss={styles.sourcesContentContainer} space="space.200">
					<Text size="small" color="color.text.subtlest">
						{formatMessage(i18n.sourcesDescription)}
					</Text>
					<Stack space="space.200">
						<Heading size="xxsmall">{formatMessage(i18n.thisPageHeader)}</Heading>
						<Flex xcss={styles.cardContainer} alignItems="center">
							{activeContentUrl && (
								<Card url={activeContentUrl} appearance="inline" truncateInline />
							)}
						</Flex>
					</Stack>
					<Stack space="space.200">
						<Heading size="xxsmall">{formatMessage(i18n.additionalSourcesHeader)}</Heading>
						<Box>
							<Stack space="space.075">
								{additionalResources.map((resource) => (
									<AdditionalResourceSelection
										key={resource.url}
										resource={resource}
										removeResource={removeAdditionalResource}
									/>
								))}
							</Stack>
							<BriefingSourcesInput
								cloudId={cloudId}
								additionalResources={additionalResources}
								addAdditionalResource={addAdditionalResource}
								activeContent={activeContent}
							/>
						</Box>
					</Stack>
					{shouldShowRestartButton && (
						<RestartBriefingButton audioRef={audioRef} source="sourcesPanel" />
					)}
				</Stack>
			</Stack>
		</Box>
	);

	return fg('confluence_smart_card_provider_preview_panel') ? (
		content
	) : (
		<ConfluenceSmartCardProvider {...smartCardProviderProps}>{content}</ConfluenceSmartCardProvider>
	);
};
